/**
 * The decisions behind the Dispatch page, in one place, with the pure rules that carry them.
 *
 * Read this first in a new session. Each rule below names where it is enforced; the tests in
 * `web/test/unit/dispatchDecisions.test.ts` state every one of them as an assertion.
 *
 * **Reading and acknowledging are two layers, and the page works from the second.** Reading is a receipt about a
 * person, read one message at a time: `web/src/models/actions/readEvents.ts` holds the per-item model's three event
 * lists and `calculateUnreads.tsx` applies them, team dispatching mode included. The per-item model applies only to
 * Dispatch-enabled workspaces (`readModelForWorkspace`); every other workspace keeps the watermark from before this
 * page existed, where seeing or opening a channel reads everything older. Acknowledging is a claim about the work,
 * shared by the workspace and attributed to whoever made it: the acknowledgement log, one table per subject (a channel
 * watermark, a message, a stop event), synced by `models/acknowledgement/` and folded by
 * `@storyboard/dispatch-view/acknowledgement`. What can wait on a dispatcher at all is the view's Requires
 * Acknowledgement list (`@storyboard/dispatch-view/requiresAcknowledgement`): the driver's messages by default, and
 * any TMS event kinds the view names. The red dot, the default "Unacknowledged only" filter, and every A key are about
 * acknowledgement; the table paints no read state at all. Reading happens where a message is shown: the row's own
 * Message cell shows the driver's message, so highlighting the one row reads it, and a `Read Feed Item` goes out for
 * that message at either grain unless the member had read it (`useReadHighlighted`, over `unreadMessageShownBy`, keyed
 * by {@link shownMessageKey}); ticked rows, however many, read nothing. Opening History sends seen events as the
 * channel pane always did. Neither layer writes to the other: A acknowledges. The acknowledgement layer will replace
 * team dispatching one day; until then both stand.
 *
 * **Acknowledgements are a stack, per target.** An acknowledge pushes, an unacknowledge pops, so taking back a
 * channel acknowledgement restores the one before it. A channel acknowledgement is a watermark through the newest
 * arrival the page held for the channel, never fanned out to its items; it covers the driver's messages and the
 * required TMS events that arrived at or before it. An item unacknowledged on its own after the watermark is free of
 * it. Anything older than a week is handled by age.
 *
 * **Acknowledge follows the row's grain, and the grain is the member's to change.** The view says whether a row is a
 * driver or a message, Display lets the member override it in the view's state, and `resolveDispatchView` in
 * `@storyboard/dispatch-view/resolve` settles it, the one place state lays over a view. {@link acknowledgeGrainFor}:
 * on a driver-per-row view, A and the header's "Acknowledge" handle the driver's channel; on a message-per-row
 * view they handle the one message. Shift+M always handles the channel; Shift+A takes the row's
 * acknowledgement back at the same grain. The keys are rebindable per view; the grain is not. A view's filters follow
 * the grain too: on a message row `unread` and `unacknowledged` mean the message (`projectDispatchItemRecord`), so
 * "Unacknowledged only" lists the waiting messages, not every message of a driver with one waiting.
 *
 * **Every acknowledgement can be undone.** Ctrl or Cmd with Z writes the inverse of the last action this session took,
 * up to {@link UNDO_LIMIT} deep, so a wrong A costs one keystroke; an action over ticked rows is one step. The inverse is
 * a row like any other, so a colleague sees the same thing.
 *
 * **History is the page's own thread, and acknowledgements show in it as events.** `components/Dispatch/thread/`
 * is a fork of the channel view for this page alone (the driver list's channel components are untouched); it reads
 * the same store and sends through the same publish. `dispatchThread.ts` builds it: messages oldest first, the
 * driver's on the left and everyone else's on the right with the driver's Seen and Heard (or Played) receipts, and
 * between them the events. Channel acknowledgements are the first events, in time order, each naming who handled the channel; a
 * message's own acknowledgement is not an event but part of the message's activity, the list behind the caption's
 * activity button that says who saw, heard, and acknowledged that message. Only standing acknowledgements appear
 * anywhere, so an undone one leaves no trace, as the stack intends. A TMS event the view requires is an item like a
 * message: a row at the message grain, a count on the driver's row, acknowledged against its anchor; every other event
 * stays a chip in the thread.
 *
 * **A message-per-row view is the store's messages within the lookback, and its rows are about the message.** On a
 * message row every driver-level kind (`recent_message`, `latest_item_at`, `unread`, `unacknowledged`) means the
 * message (`projectDispatchItemRecord`), so a view laid out for drivers reads correctly once the grain is switched.
 * Rows stop at the acknowledgement lookback (`computeDispatchItems`), so loading a channel's history, as opening its
 * thread does, cannot grow the table; and the open driver's exemption from the filters is a driver-row rule only, so
 * opening a thread never surfaces every message of that driver.
 *
 * **Opening History never acknowledges; a reply does.** Reading a channel is not handling it, but answering it is: A
 * or the row actions acknowledge, opening a thread only reads, and a reply sent from this page acknowledges too. A
 * reply from the row writes the acknowledgement A would for that row, at the view's grain, and hands the selection on
 * as A does. A reply from History or Context always acknowledges the driver's channel, whatever the grain, and leaves
 * the selection and the cursor where they are, since the dispatcher is mid-conversation. A fleet message acknowledges nothing. The keymap and the
 * shortcut bar's fold are page settings: they belong to the page, not to any view.
 *
 * **The page is keyboard-first.** The highlight never strands: when its row leaves the list (acknowledged, filtered
 * out, a teammate cleared it) the highlight lands on the row now in that position ({@link highlightIndexAfterRemoval});
 * handling a row, by A or by a reply from the row, advances to the row after it in the list as it stood when the key or
 * Send was pressed, so a row the handling regroups or filters out cannot change where the selection lands. H and C toggle what
 * they opened, and what they opened follows the selection: a key, a click, or handling the row before it that lands
 * on another driver moves the open tab to them (`followedPanelPath` in `dispatchPanelRoute.ts`), a row vanishing
 * under the highlight does not; F opens the fleet composer; Escape closes what is open, then clears the ticked rows.
 * R replies from where the dispatcher is looking: the open panel's composer, one under both tabs so its draft
 * survives a tab switch, else the highlighted row's. In any composer the first Escape only leaves the box, handing the
 * keys back to the table, and the next one closes what is open.
 *
 * **Ticking rows is a mode, like a spreadsheet's selection.** Any ticked row folds the detail away and makes A and
 * Shift+M act on the ticked rows; H and C go quiet, and R too unless a panel is open; Space ticks the highlighted
 * row, Shift with an arrow (or J or K) extends the range, Ctrl or Cmd with A ticks every row, Escape clears. With
 * nothing highlighted, a Shift run still finds a row to start from: the open panel's driver, else the row under the
 * pointer, else the end of the list the arrow points away from (`highlightAnchorIndex`). Row text is not selectable,
 * so no click or drag leaves a text anchor for those keys to grow instead. `useDispatchShortcuts` and `DispatchView`
 * carry it.
 *
 * **A fleet message goes to the rows in front of you.** `fleetRecipients.ts`: the ticked drivers when any are ticked,
 * else every driver the filters leave visible, with those filters repeated as chips in the composer; a few are named,
 * many are counted. The dashboard is where recipients are chosen, so the composer only says who and takes the
 * message. The old fleet message experience is untouched and shares only the broadcast underneath.
 *
 * **Feed groups are being retired.** The Dispatch experience never offers the first-class group: not as a column, a
 * filter, or a recipient list. A tag that happens to be called "group" is just a tag.
 *
 * **A field with no column is filtered from the Filter menu.** A column's header is where a field is filtered, so
 * every field that narrows rows yet never shows as a column (the acknowledgement layer) gets an editor of its own in
 * the Filter popover, unless a control of the view already targets it (`filterMenuFields`): the built-in view's
 * "Unacknowledged only" toggle is that field's editor there, and two editors on one field could compose to no rows
 * while both look active. The editor writes the same ad-hoc conditions a header editor would, so the strip's chips
 * and Clear all read it the same way.
 *
 * **Channels are a setting, not a filter.** The team picker decides which of a driver's channels the rows read and
 * open; the alias is never a filter or a grouping. A driver with several channels is several rows only across teams.
 *
 * **Open question, deliberately unsolved:** on an "Unacknowledged only" view, acknowledging a row removes it, which is
 * the point; but a dispatcher who wants to glance back at what they just handled has nowhere to look but the filter
 * toggle. Something between "gone" and "shown" (a grace period, a recently-handled shelf) may be wanted. Nothing here
 * depends on the answer.
 */

import type { DispatchGrain } from "@storyboard/dispatch-view/view";
import type { DispatchTableRow } from "./dispatchTableRow";

/**
 * What an acknowledgement acts on: a driver's whole channel, or one message.
 *
 * The page's vocabulary, one word per concept: the roving row the keyboard acts on is the *highlight*; the checkboxes
 * are *ticks*, and rows with a tick are *ticked*; the view's top-bar filters are *controls*, their live values the
 * *control values*; a row is a *driver* or a *message* (the store calls a message an `Item`, and the stored field kinds
 * keep that spelling: `item_text`).
 */
export type AcknowledgeGrain = "channel" | "message";

/**
 * The grain M and the header's "Acknowledge" act at: the channel when rows are drivers, the one message when
 * rows are messages. The row grain itself is the resolved view's (`resolveDispatchView`): the member's over the view's,
 * a driver per row when neither says.
 */
export function acknowledgeGrainFor(grain: DispatchGrain): AcknowledgeGrain {
  return grain === "message" ? "message" : "channel";
}

/** How many acknowledgements Ctrl/Cmd+Z can take back, newest first. */
export const UNDO_LIMIT = 50;

/**
 * What a row's Message cell shows, so a highlight reads again only when there is something new to read: the message
 * on a message row, on a driver row the time of what its Message cell shows (the driver's latest message, or a newer
 * required TMS event), and on a task row its item, else the task.
 */
export function shownMessageKey(row: DispatchTableRow): string {
  if (row.kind === "message") return row.item.id;
  if (row.kind === "task") return row.item?.id ?? row.task.task.id;
  return row.driver.latestItemCreatedAt ?? "";
}

/**
 * Where the highlight lands once the row it was on has left the list: the row now in that position, or the last row
 * when the list got shorter than that, or nowhere when the list is empty. The dispatcher's hands stay on the keys.
 */
export function highlightIndexAfterRemoval(previousIndex: number, rowCount: number): number | null {
  if (rowCount <= 0 || previousIndex < 0) return null;
  return Math.min(previousIndex, rowCount - 1);
}
